import {
  ApplicantStatus,
  ExecutionModes,
  RetryIdentityVerificationStatus,
  Verification as VerificationA,
  VerificationBypassOutcome,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import {
  Verification as VerificationB,
  VerificationSummary
} from "views/applications-id/components/ApplicantsTable/types";
import { Applicant, Application } from "types/application";

type GeneralVerification = Partial<VerificationA | VerificationB>;

export type ReportState =
  // Blocked
  | "idv_override_required"
  | "warnings_override_required"
  | "eviction_blocked_by_credit"
  | "idv_retrying"
  | "idv_bypassed_not_run"
  // Execution mode states
  | "manual_pending"
  | "conditional_pending"
  | "conditional_skipped"
  // In progress
  | "rerun_in_progress"
  | "submitted_in_progress"
  | "processing"
  | "started"
  | "pending"
  // Terminal
  | "canceled"
  | "no_data_available"
  | "finished"
  | "failed"
  | "finished_with_warnings"
  | "skipped"
  // Defer to slice (for ReportsCard)
  | "render_report"
  | "render_credit_report"
  | "render_combined_income_report"
  | "render_pet_screening"
  | "render_warnings_report"
  | "render_enhanced_criminal_report";

type BypassFields = Partial<
  Pick<
    VerificationA,
    "name" | "status" | "bypass_request" | "bypass_resolution"
  >
>;

export const hasIdentityVerificationBypassToShow = (
  verification?: BypassFields
): boolean => {
  if (verification?.name !== VerificationNames.IdentityVerification)
    return false;

  const { status, bypass_request, bypass_resolution } = verification;

  if (status === VerificationStatus.FinishedByBypassed) return true;
  if (bypass_request) return true;

  return (
    bypass_resolution?.outcome === VerificationBypassOutcome.Declined &&
    [VerificationStatus.Pending, VerificationStatus.Started].includes(
      status as VerificationStatus
    )
  );
};

const IDV_BYPASSED_SKIP_REASON =
  "This report wasn't run because identity verification was bypassed before Boom received the applicant's date of birth and SSN.";

const isNotRunForBypassedIdentityVerification = (
  verification?: Partial<Pick<VerificationA, "status" | "skip_reason">>
): boolean =>
  verification?.status === VerificationStatus.Skipped &&
  verification.skip_reason === IDV_BYPASSED_SKIP_REASON;

interface VerificationStateHandlerOptions {
  verification?: GeneralVerification;
  applicant?: Applicant;
  application?: Application;
}

export class VerificationStateHandler {
  private verification?: GeneralVerification;
  private applicant?: Applicant;
  private application?: Application;

  constructor(opts: VerificationStateHandlerOptions) {
    this.verification = opts.verification;
    this.applicant = opts.applicant;
    this.application = opts.application;
  }

  private isIdentityVerificationRetrying = (): boolean => {
    if (this.verification?.name !== VerificationNames.IdentityVerification)
      return false;

    const isPendingOrStarted =
      this.verification?.status === VerificationStatus.Pending ||
      this.verification?.status === VerificationStatus.Started;

    const isRetryActive = [
      RetryIdentityVerificationStatus.Pending,
      RetryIdentityVerificationStatus.InProgress
    ].includes(
      this.applicant?.retry_identity_verification_status ||
        RetryIdentityVerificationStatus.NotAvailable
    );

    return isPendingOrStarted && isRetryActive;
  };

  public isNotRunForBypassedIdentityVerification = (): boolean =>
    isNotRunForBypassedIdentityVerification(this.verification);

  private isEvictionBlockedByCredit = (): boolean => {
    if (this.verification?.name !== VerificationNames.EvictionReport)
      return false;
    if (this.verification.status !== VerificationStatus.Pending) return false;
    if (this.applicant?.application_status !== ApplicantStatus.Submitted)
      return false;
    if (this.verification.settings?.execution_mode === ExecutionModes.Manual)
      return false;

    const creditReport = this.applicant.verifications?.find(
      (v) => v.name === VerificationNames.CreditReport
    );

    return (
      !!creditReport &&
      [
        VerificationStatus.Failed,
        VerificationStatus.FinishedWithWarnings
      ].includes(creditReport.status)
    );
  };

  private isBlockedByIdentityOverride = (): boolean => {
    if (!this.verification) return false;

    const isIdvOverrideRequired =
      this.application?.requires_overrides_identity_verification?.applicants.some(
        ({ id }) => id === this.applicant?.id
      );
    if (!isIdvOverrideRequired || !this.verification.name) return false;

    return [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EnhancedCriminalReport,
      VerificationNames.EvictionReport
    ].includes(this.verification.name);
  };

  // A failed premium warnings report pauses every paid report that has not run
  // yet; they resume once a partner user overrides it from the Warnings report.
  private isBlockedByWarningsOverride = (): boolean => {
    if (!this.verification) return false;
    if (this.verification.status !== VerificationStatus.Pending) return false;

    const isWarningsOverrideRequired =
      this.application?.requires_override_warnings_report?.applicants.some(
        ({ id }) => id === this.applicant?.id
      );
    if (!isWarningsOverrideRequired || !this.verification.name) return false;

    return [
      VerificationNames.CreditReport,
      VerificationNames.CriminalReport,
      VerificationNames.EvictionReport
    ].includes(this.verification.name);
  };

  private isReportProcessing = (): boolean => {
    // PetScreening and WarningsReport render their own in-progress states
    if (
      this.verification?.name === VerificationNames.PetScreeningReport ||
      this.verification?.name === VerificationNames.WarningsReport
    )
      return false;

    const isCurrentlyProcessing =
      this.verification?.status === VerificationStatus.Processing;

    const isVerifiedIncomeProcessing =
      this.verification?.name === VerificationNames.IncomeVerification &&
      this.applicant?.verifications.find(
        (v) => v.name === VerificationNames.VerifiedIncomeVerification
      )?.status === VerificationStatus.Processing;

    return isCurrentlyProcessing || isVerifiedIncomeProcessing;
  };

  public getCreditReportStatus = () => {
    const activeCreditVerification =
      this.applicant?.credit_report_verifications?.find(
        (cr) => !cr.archived_at
      );
    return activeCreditVerification?.status ?? VerificationStatus.Pending;
  };

  /*
   * isRender - Whether the function is being called for an actual state
   *            (ReportStatusBadge) or for rendering logic (ReportsCard)
   */
  public getReportState = (opts?: {
    isRender: boolean;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }): ReportState | VerificationStatus => {
    const { isRender } = opts || {};

    // A failed premium warnings report pauses every paid report that has not
    // run yet. Checked before everything else: the credit report otherwise
    // short-circuits into its own slice (which renders an in-progress view),
    // and manual/conditional reports cannot run while the gate is up either.
    if (this.isBlockedByWarningsOverride()) return "warnings_override_required";

    if (this.verification?.name === VerificationNames.CreditReport) {
      if (isRender && this.isNotRunForBypassedIdentityVerification())
        return "idv_bypassed_not_run";

      return isRender ? "render_credit_report" : this.getCreditReportStatus();
    }

    if (
      this.verification?.name === VerificationNames.CombinedIncome &&
      isRender
    )
      return "render_combined_income_report";

    if (this.isIdentityVerificationRetrying()) return "idv_retrying";

    if (
      isRender &&
      hasIdentityVerificationBypassToShow(this.verification as BypassFields)
    )
      return "render_report";

    if (
      this.verification?.status === VerificationStatus.Pending &&
      this.verification.settings?.execution_mode === ExecutionModes.Manual
    )
      return "manual_pending";

    const isPendingOrSkipped =
      this.verification?.status === VerificationStatus.Pending ||
      this.verification?.status === VerificationStatus.Skipped;
    if (
      this.verification?.settings?.execution_mode ===
        ExecutionModes.Conditionally &&
      isPendingOrSkipped
    ) {
      const conditionalStatus = this.verification.status as
        | VerificationStatus.Pending
        | VerificationStatus.Skipped;
      return `conditional_${conditionalStatus}`;
    }

    if (this.isNotRunForBypassedIdentityVerification())
      return "idv_bypassed_not_run";

    if (this.isEvictionBlockedByCredit()) return "eviction_blocked_by_credit";

    const isAwaitingApplicantSubmission =
      this.verification?.status === VerificationStatus.Pending &&
      this.applicant?.application_status !== ApplicantStatus.Submitted;

    // Enhanced criminal renders its own per-search pending tabs
    if (
      this.verification?.name === VerificationNames.EnhancedCriminalReport &&
      ![
        VerificationStatus.Canceled,
        VerificationStatus.NoDataAvailable
      ].includes(this.verification.status as VerificationStatus) &&
      !isAwaitingApplicantSubmission &&
      !this.isBlockedByIdentityOverride() &&
      isRender
    )
      return "render_enhanced_criminal_report";

    if (
      this.verification?.name === VerificationNames.CanadianCriminalReport &&
      this.verification.summary?.report_state &&
      isRender
    )
      return "render_report";

    if (
      this.verification?.status === VerificationStatus.Pending &&
      this.verification.name !== VerificationNames.PetScreeningReport
    ) {
      if (this.verification.is_rerun) return "rerun_in_progress";
      if (this.applicant?.application_status === ApplicantStatus.Submitted)
        return "submitted_in_progress";
    }

    if (this.isBlockedByIdentityOverride()) return "idv_override_required";

    if (this.isReportProcessing()) return "processing";

    if (
      this.verification?.name === VerificationNames.PetScreeningReport &&
      isRender
    )
      return "render_pet_screening";

    // Warnings report is a derived/computed report: skip the per-verification
    // status fallbacks (Pending/Started/NoDataAvailable) because it has no
    // upstream vendor and is always available once an applicant exists. A
    // canceled one still reads as canceled, like every other report — a premium
    // report would otherwise offer an override for a canceled applicant.
    if (
      this.verification?.name === VerificationNames.WarningsReport &&
      this.verification.status !== VerificationStatus.Canceled &&
      isRender
    )
      return "render_warnings_report";

    if (
      this.verification?.status &&
      [
        VerificationStatus.Pending,
        VerificationStatus.Canceled,
        VerificationStatus.Started,
        VerificationStatus.NoDataAvailable,
        VerificationStatus.Failed,
        VerificationStatus.FinishedWithWarnings,
        VerificationStatus.Skipped
      ].includes(this.verification.status)
    )
      return this.verification.status;

    if (isRender) return "render_report";

    return "finished";
  };

  /*
   * ReportStatusBadge specific functions -- not relevant in ReportsCard
   */
  public isCriminalApprovalRequired = (): boolean => {
    if (
      !this.verification?.name ||
      ![
        VerificationNames.CriminalReport,
        VerificationNames.EnhancedCriminalReport
      ].includes(this.verification.name)
    )
      return false;

    const summary = this.verification.summary as
      | VerificationSummary
      | undefined;
    return Boolean(summary?.require_approval);
  };
}
